import React, { FC, Suspense, useEffect, useId, useState } from 'react';
import { graphql, useLazyLoadQuery, useRelayEnvironment } from 'react-relay/hooks';
import { T } from 'src/shared/helpers';
import { CustomErrorBoundary } from '../customErrorBoundary';
import { NotificationBanner } from '../notificationBanner';
import { waitForProductExists_checkProductExistsQuery } from './__generated__/waitForProductExists_checkProductExistsQuery.graphql';

type WaitForProductExistsProps = {
    productId: string;
    fallback?: React.ReactNode;
    children: React.ReactNode;
};

const ProductAvailability: FC<WaitForProductExistsProps & { requestId: string }> = ({ productId, fallback, children, requestId }) => {
    const [poll, setPoll] = useState(0);
    const response = useLazyLoadQuery<waitForProductExists_checkProductExistsQuery>(
        graphql`
            query waitForProductExists_checkProductExistsQuery($productId: String!) {
                existsProductOnDesignService(productId: $productId)
                existsProductOnInterfaceService(productId: $productId)
            }
        `,
        { productId },
        // A new visit/retry must check the network, even if Relay retains an earlier successful check.
        { fetchPolicy: 'network-only', fetchKey: `${requestId}:${poll}` }
    );
    const ready = response.existsProductOnDesignService && response.existsProductOnInterfaceService;
    useEffect(() => {
        if (ready) return;
        const timer = setTimeout(() => setPoll((value) => value + 1), 500);
        return () => clearTimeout(timer);
    }, [ready, poll]);

    return <>{ready ? children : fallback}</>;
};

const ProductAvailabilityAttempt: FC<WaitForProductExistsProps> = (props) => {
    // Keep the visit identity above the suspending query so initial render retries reuse it.
    const requestId = useId();
    return (
        <Suspense fallback={props.fallback}>
            <ProductAvailability {...props} requestId={requestId} />
        </Suspense>
    );
};

const WaitForProductExists: FC<WaitForProductExistsProps> = (props) => {
    const environment = useRelayEnvironment();
    const [scope, setScope] = useState({ environment, version: 0 });
    const [retry, setRetry] = useState(0);
    // Reset before rendering children, rather than briefly reusing readiness from another environment.
    if (scope.environment !== environment) setScope({ environment, version: scope.version + 1 });
    return (
        <CustomErrorBoundary
            key={`${props.productId}:${scope.version}:${retry}`}
            render={(error) => {
                if (error?.message === 'Unauthenticated seller') throw error;
                return (
                    <NotificationBanner
                        type='ERROR'
                        content={T._('Unable to check whether the product is ready. Please try again.', 'Admin')}
                        buttons={[{ label: T._('Retry', 'Admin'), onClick: () => setRetry((value) => value + 1) }]}
                    />
                );
            }}
        >
            <ProductAvailabilityAttempt {...props} />
        </CustomErrorBoundary>
    );
};

export default WaitForProductExists;
